"use client";

import {
  Toaster as ChakraToaster,
  createToaster,
  HStack,
  Icon,
  List,
  Portal,
  Spinner,
  Stack,
  Text,
  Toast,
} from "@chakra-ui/react";
import {
  LuCheckCheck,
  LuCircleAlert,
  LuInfo,
  LuOctagonX,
} from "react-icons/lu";
import { getApiResponseMapping } from "../../state/utils/responseHandling";
import type { ApiCodes } from "../../types/apiResponseType";
import { OverlayDialog } from "./overlay_dialog";

export const toaster = createToaster({
  placement: "bottom-end",
  pauseOnPageIdle: true,
  max: 3,
  duration: 6000,
  overlap: true,
  offsets: { left: "20px", top: "20px", right: "20px", bottom: "20px" },
});

interface ToastApiProps {
  type?: "success" | "warning" | "error" | "info";
  status?: keyof ApiCodes;
  code?: string;
  message?: string;
  detail?: string;
  showDetails?: string | string[];
}

const getTypeValue = (type: "success" | "warning" | "error" | "info") => {
  switch (type) {
    case "success":
      return { icon: LuCheckCheck, color: "fg.success", title: "Success" };
    case "warning":
      return { icon: LuCircleAlert, color: "fg.warning", title: "Warning" };
    case "error":
      return { icon: LuOctagonX, color: "fg.error", title: "Error" };
    case "info":
      return { icon: LuInfo, color: "fg.info", title: "Info" };
  }
};
export const toasterApi = ({
  type = "success",
  status = 200,
  code,
  message,
  detail,
  showDetails,
}: ToastApiProps) => {
  const { icon, color, title } = getTypeValue(type);

  const responseMapping = getApiResponseMapping(status, code);
  const description = message
    ? `${message || responseMapping.message}!`
    : detail
    ? `${detail || responseMapping.detail}!`
    : "";

  toaster.create({
    type: type,
    title: title,
    description: description,
    action:
      showDetails &&
      ((Array.isArray(showDetails) && showDetails.length > 0) ||
        (typeof showDetails === "string" && showDetails.length > 0))
        ? {
            label: "Show Details",
            onClick: () => {
              OverlayDialog.open("showDetails", {
                overlayId: "showDetails",
                type: type,
                title: (
                  <HStack color={color} gap={5}>
                    <Icon as={icon} />
                    {title}
                  </HStack>
                ),
                description: (
                  <HStack color={"fg.subtle"} gap={5}>
                    <Icon as={icon} />
                    {`Detailed information regarding the $
                    {title.toLowerCase() || 'this'} message!`}
                  </HStack>
                ),
                content: (
                  <>
                    {Array.isArray(showDetails) && showDetails.length > 0 ? (
                      <List.Root>
                        {showDetails.map((item, index) => (
                          <List.Item key={`detail-${index}`}>{item}</List.Item>
                        ))}
                      </List.Root>
                    ) : (
                      <Text key="detail-0" textStyle="fieldContent">
                        {showDetails}
                      </Text>
                    )}
                  </>
                ),
              });
            },
          }
        : undefined,
  });
};

export const Toaster = () => {
  return (
    <Portal>
      <ChakraToaster toaster={toaster} insetInline={{ mdDown: "4" }}>
        {(toast) => {
          return (
            <Toast.Root width={{ md: "sm" }}>
              {toast.type === "loading" ? (
                <Spinner size="sm" color="blue.solid" />
              ) : (
                <Toast.Indicator />
              )}
              <Stack gap="1" flex="1" maxWidth="100%">
                {toast.title && <Toast.Title>{toast.title}</Toast.Title>}
                {toast.description && (
                  <Toast.Description>{toast.description}</Toast.Description>
                )}
              </Stack>
              {toast.action && (
                <Toast.ActionTrigger>{toast.action.label}</Toast.ActionTrigger>
              )}
              {toast.meta?.closable && <Toast.CloseTrigger />}
            </Toast.Root>
          );
        }}
      </ChakraToaster>
    </Portal>
  );
};
